/** Closed, value-free revision algebra. No runtime, SDK, database or result imports. */
import {
  PANEL_REGISTRY, PANEL_REGISTRY_V3, RECIPE_ROOT_KEYS, RECIPE_PANEL_KEYS, CANONICAL_TRAJECTORY_MODES,
  DEFAULT_TRAJECTORY_MODE, canonicalizeHistoricalDashboardRecipe, stableJson, sha256Hex,
} from './historical-dashboard-contract.js';

export const HISTORICAL_DASHBOARD_RECIPE_V2_SCHEMA = 'historical-dashboard-recipe/v2';
export const HISTORICAL_DASHBOARD_PATCH_V2_SCHEMA = 'historical-dashboard-patch/v2';
export const HISTORICAL_DASHBOARD_ROOT_V2_QUERY_KEY = 'historical.dashboard-composer.v2';
export const HISTORICAL_DASHBOARD_METHOD_V2_VERSION = 'dashboard-v2';
export const HISTORICAL_DASHBOARD_RECIPE_V3_SCHEMA = 'historical-dashboard-recipe/v3';
export const HISTORICAL_DASHBOARD_PATCH_V3_SCHEMA = 'historical-dashboard-patch/v3';
export const HISTORICAL_DASHBOARD_ROOT_V3_QUERY_KEY = 'historical.dashboard-composer.v3';
export const HISTORICAL_DASHBOARD_METHOD_V3_VERSION = 'dashboard-v3';
export const HISTORICAL_ENTITY_CODES = Object.freeze(['MOMENTUS', 'MONTA', 'BUILT_TECHNOLOGIES', 'RELAY', 'OPENSPACE', 'RHOMBUS', 'GETCOVERED']);
export const HISTORICAL_PERIODS = Object.freeze(['full_history', 'since_entry', 'latest_three_years', 'latest_five_years']);
export const HISTORICAL_DASHBOARD_ROOT_V2_POLICY = Object.freeze({
  period_policy: 'calendar_years_first_actual_v1',
  view_policy: 'owned_preference_both_trajectory_modes_v1',
  v1_compatibility: 'same_children_dataset_default_asof_entry_v1',
});
export const HISTORICAL_DASHBOARD_ROOT_V3_POLICY = Object.freeze({
  ...HISTORICAL_DASHBOARD_ROOT_V2_POLICY,
  v2_compatibility: 'same_original_five_children_dataset_default_asof_entry_v1',
});
const PANEL_KEYS = PANEL_REGISTRY.map(p => p.panelKey);
const PANEL_KEYS_V3 = PANEL_REGISTRY_V3.map(p => p.panelKey);
const V2_KEYS = [...RECIPE_ROOT_KEYS, 'entityCodes', 'period', 'viewPreferences'];
const V3_KEYS = V2_KEYS;
const pair = p => Object.fromEntries(RECIPE_PANEL_KEYS.map(k => [k, p[k]]));
const same = (a, b) => stableJson(a) === stableJson(b);
const clone = value => JSON.parse(JSON.stringify(value));

export function historicalDashboardPatchDenied() {
  const error = new Error('Historical dashboard patch is unavailable.');
  error.status = 422;
  error.code = 'historical_dashboard_patch_denied';
  return error;
}
function demand(condition) { if (!condition) throw historicalDashboardPatchDenied(); }
function closed(value, keys) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    && [Object.prototype, null].includes(Object.getPrototypeOf(value))
    && Reflect.ownKeys(value).length === keys.length && keys.every(k => Object.hasOwn(value, k));
}
function enumList(value, allowed, min = 1, max = allowed.length) {
  demand(Array.isArray(value) && value.length >= min && value.length <= max);
  demand(new Set(value).size === value.length && [...value].every(v => allowed.includes(v)));
  return [...value];
}
function entities(value) {
  const selected = enumList(value, HISTORICAL_ENTITY_CODES);
  return HISTORICAL_ENTITY_CODES.filter(code => selected.includes(code));
}
function mode(value) { demand(CANONICAL_TRAJECTORY_MODES.includes(value)); return value; }
function period(value) { demand(HISTORICAL_PERIODS.includes(value)); return value; }
function panels(value, registry = PANEL_REGISTRY, max = 5) {
  demand(Array.isArray(value) && value.length >= 1 && value.length <= max);
  const seen = new Set();
  return [...value].map(p => {
    demand(closed(p, RECIPE_PANEL_KEYS));
    const expected = registry.find(e => e.panelKey === p.panelKey);
    demand(expected && !seen.has(p.panelKey) && RECIPE_PANEL_KEYS.every(k => p[k] === expected[k]));
    seen.add(p.panelKey);
    return pair(expected);
  });
}

export function canonicalizeHistoricalDashboardRecipeV2(value) {
  demand(closed(value, V2_KEYS) && value.schemaVersion === HISTORICAL_DASHBOARD_RECIPE_V2_SCHEMA);
  demand(value.datasetKey === 'techfund_operating_history' && value.cohortKey === 'greenpoint_core_seven'
    && value.metricKey === 'revenue' && value.basis === 'actual_point_in_time'
    && value.supportingTable === 'follow_selected_panel' && value.trace === 'available_collapsed');
  demand(closed(value.viewPreferences, ['trajectoryMode']));
  return {
    schemaVersion: HISTORICAL_DASHBOARD_RECIPE_V2_SCHEMA,
    datasetKey: value.datasetKey, cohortKey: value.cohortKey, metricKey: value.metricKey, basis: value.basis,
    panels: panels(value.panels), supportingTable: value.supportingTable, trace: value.trace,
    entityCodes: entities(value.entityCodes), period: period(value.period),
    viewPreferences: { trajectoryMode: mode(value.viewPreferences.trajectoryMode) },
  };
}
export function adaptHistoricalDashboardRecipeV1(value) {
  let recipe;
  try { recipe = canonicalizeHistoricalDashboardRecipe(value); } catch { throw historicalDashboardPatchDenied(); }
  return canonicalizeHistoricalDashboardRecipeV2({ ...recipe, schemaVersion: HISTORICAL_DASHBOARD_RECIPE_V2_SCHEMA,
    entityCodes: [...HISTORICAL_ENTITY_CODES], period: 'full_history', viewPreferences: { trajectoryMode: DEFAULT_TRAJECTORY_MODE } });
}
export function effectiveHistoricalDashboardRecipe(value) {
  if (value?.schemaVersion === 'historical-dashboard-recipe/v1') return adaptHistoricalDashboardRecipeV1(value);
  if (value?.schemaVersion === HISTORICAL_DASHBOARD_RECIPE_V2_SCHEMA) return canonicalizeHistoricalDashboardRecipeV2(value);
  throw historicalDashboardPatchDenied();
}

export function canonicalizeHistoricalDashboardRecipeV3(value) {
  demand(closed(value, V3_KEYS) && value.schemaVersion === HISTORICAL_DASHBOARD_RECIPE_V3_SCHEMA);
  demand(value.datasetKey === 'techfund_operating_history' && value.cohortKey === 'greenpoint_core_seven'
    && value.metricKey === 'revenue' && value.basis === 'actual_point_in_time'
    && value.supportingTable === 'follow_selected_panel' && value.trace === 'available_collapsed');
  demand(closed(value.viewPreferences, ['trajectoryMode']));
  return {
    schemaVersion: HISTORICAL_DASHBOARD_RECIPE_V3_SCHEMA,
    datasetKey: value.datasetKey, cohortKey: value.cohortKey, metricKey: value.metricKey, basis: value.basis,
    panels: panels(value.panels, PANEL_REGISTRY_V3, 13), supportingTable: value.supportingTable, trace: value.trace,
    entityCodes: entities(value.entityCodes), period: period(value.period),
    viewPreferences: { trajectoryMode: mode(value.viewPreferences.trajectoryMode) },
  };
}
export function adaptHistoricalDashboardRecipeV2(value) {
  const recipe = canonicalizeHistoricalDashboardRecipeV2(value);
  return canonicalizeHistoricalDashboardRecipeV3({ ...recipe, schemaVersion: HISTORICAL_DASHBOARD_RECIPE_V3_SCHEMA });
}
export function effectiveHistoricalDashboardRecipeV3(value) {
  if (value?.schemaVersion === HISTORICAL_DASHBOARD_RECIPE_V3_SCHEMA) return canonicalizeHistoricalDashboardRecipeV3(value);
  if (value?.schemaVersion === HISTORICAL_DASHBOARD_RECIPE_V2_SCHEMA) return adaptHistoricalDashboardRecipeV2(value);
  if (value?.schemaVersion === 'historical-dashboard-recipe/v1') return adaptHistoricalDashboardRecipeV2(adaptHistoricalDashboardRecipeV1(value));
  throw historicalDashboardPatchDenied();
}
export function effectiveHistoricalDashboardRecipeAny(value) {
  if (value?.schemaVersion === HISTORICAL_DASHBOARD_RECIPE_V3_SCHEMA) return canonicalizeHistoricalDashboardRecipeV3(value);
  return effectiveHistoricalDashboardRecipe(value);
}

const OP_KEYS = Object.freeze({
  set_entities: ['operation', 'entityCodes'], set_period: ['operation', 'period'],
  add_panel: ['operation', 'panelKey'], remove_panel: ['operation', 'panelKey'],
  set_display_mode: ['operation', 'panelKey', 'displayMode'], reorder_panels: ['operation', 'order'],
});
const PHASES = Object.keys(OP_KEYS);
export function canonicalizeHistoricalDashboardPatch(value) {
  if (value?.schemaVersion === HISTORICAL_DASHBOARD_PATCH_V3_SCHEMA) return canonicalizeHistoricalDashboardPatchV3(value);
  demand(closed(value, ['schemaVersion', 'operations']) && value.schemaVersion === HISTORICAL_DASHBOARD_PATCH_V2_SCHEMA);
  demand(Array.isArray(value.operations) && value.operations.length >= 1 && value.operations.length <= 8);
  const seen = new Set(), added = new Set(), removed = new Set();
  const operations = [...value.operations].map(input => {
    demand(input && Object.hasOwn(OP_KEYS, input.operation) && closed(input, OP_KEYS[input.operation]));
    const name = input.operation, identity = `${name}:${input.panelKey || ''}`;
    demand(!seen.has(identity)); seen.add(identity);
    if (name === 'set_entities') return { operation: name, entityCodes: entities(input.entityCodes) };
    if (name === 'set_period') return { operation: name, period: period(input.period) };
    if (name === 'reorder_panels') return { operation: name, order: enumList(input.order, PANEL_KEYS) };
    demand(PANEL_KEYS.includes(input.panelKey));
    if (name === 'set_display_mode') {
      demand(input.panelKey === 'cohort_trajectory');
      return { operation: name, panelKey: input.panelKey, displayMode: mode(input.displayMode) };
    }
    (name === 'add_panel' ? added : removed).add(input.panelKey);
    return { operation: name, panelKey: input.panelKey };
  });
  demand(![...added].some(key => removed.has(key)));
  operations.sort((a, b) => PHASES.indexOf(a.operation) - PHASES.indexOf(b.operation)
    || PANEL_KEYS.indexOf(a.panelKey) - PANEL_KEYS.indexOf(b.panelKey));
  return { schemaVersion: HISTORICAL_DASHBOARD_PATCH_V2_SCHEMA, operations };
}

export function canonicalizeHistoricalDashboardPatchV3(value) {
  demand(closed(value, ['schemaVersion', 'operations']) && value.schemaVersion === HISTORICAL_DASHBOARD_PATCH_V3_SCHEMA);
  demand(Array.isArray(value.operations) && value.operations.length >= 1 && value.operations.length <= 8);
  const seen = new Set(), added = new Set(), removed = new Set();
  const operations = [...value.operations].map(input => {
    demand(input && Object.hasOwn(OP_KEYS, input.operation) && closed(input, OP_KEYS[input.operation]));
    const name = input.operation, identity = `${name}:${input.panelKey || ''}`;
    demand(!seen.has(identity)); seen.add(identity);
    if (name === 'set_entities') return { operation: name, entityCodes: entities(input.entityCodes) };
    if (name === 'set_period') return { operation: name, period: period(input.period) };
    if (name === 'reorder_panels') return { operation: name, order: enumList(input.order, PANEL_KEYS_V3) };
    demand(PANEL_KEYS_V3.includes(input.panelKey));
    if (name === 'set_display_mode') {
      demand(input.panelKey === 'cohort_trajectory');
      return { operation: name, panelKey: input.panelKey, displayMode: mode(input.displayMode) };
    }
    (name === 'add_panel' ? added : removed).add(input.panelKey);
    return { operation: name, panelKey: input.panelKey };
  });
  demand(![...added].some(key => removed.has(key)));
  operations.sort((a, b) => PHASES.indexOf(a.operation) - PHASES.indexOf(b.operation)
    || PANEL_KEYS_V3.indexOf(a.panelKey) - PANEL_KEYS_V3.indexOf(b.panelKey));
  return { schemaVersion: HISTORICAL_DASHBOARD_PATCH_V3_SCHEMA, operations };
}

export function applyHistoricalDashboardPatch(baseRecipe, input) {
  if (input?.schemaVersion === HISTORICAL_DASHBOARD_PATCH_V3_SCHEMA
    || baseRecipe?.schemaVersion === HISTORICAL_DASHBOARD_RECIPE_V3_SCHEMA) {
    const patchInput = input?.schemaVersion === HISTORICAL_DASHBOARD_PATCH_V3_SCHEMA
      ? input
      : { ...input, schemaVersion: HISTORICAL_DASHBOARD_PATCH_V3_SCHEMA };
    return applyHistoricalDashboardPatchV3(baseRecipe, patchInput);
  }
  const base = effectiveHistoricalDashboardRecipe(baseRecipe), patch = canonicalizeHistoricalDashboardPatch(input);
  const recipe = clone(base), keys = new Set(base.panels.map(p => p.panelKey));
  for (const op of patch.operations) {
    if (op.operation === 'set_entities') recipe.entityCodes = [...op.entityCodes];
    if (op.operation === 'set_period') recipe.period = op.period;
    if (op.operation === 'add_panel') {
      demand(!keys.has(op.panelKey));
      recipe.panels.push(pair(PANEL_REGISTRY.find(p => p.panelKey === op.panelKey)));
      if (op.panelKey === 'cohort_trajectory') recipe.viewPreferences.trajectoryMode = DEFAULT_TRAJECTORY_MODE;
    }
    if (op.operation === 'remove_panel') {
      demand(keys.has(op.panelKey)); recipe.panels = recipe.panels.filter(p => p.panelKey !== op.panelKey);
    }
    if (op.operation === 'set_display_mode') {
      demand(recipe.panels.some(p => p.panelKey === op.panelKey)); recipe.viewPreferences.trajectoryMode = op.displayMode;
    }
    if (op.operation === 'reorder_panels') {
      demand(op.order.length === recipe.panels.length && op.order.every(key => recipe.panels.some(p => p.panelKey === key)));
      recipe.panels = op.order.map(key => recipe.panels.find(p => p.panelKey === key));
    }
  }
  const result = canonicalizeHistoricalDashboardRecipeV2(recipe);
  return { recipe: result, patch, diff: historicalDashboardRecipeDiff(base, result),
    calculationPlan: historicalDashboardCalculationPlan(base, result) };
}

export function applyHistoricalDashboardPatchV3(baseRecipe, input) {
  const base = effectiveHistoricalDashboardRecipeV3(baseRecipe), patch = canonicalizeHistoricalDashboardPatchV3(input);
  const recipe = clone(base), keys = new Set(base.panels.map(p => p.panelKey));
  for (const op of patch.operations) {
    if (op.operation === 'set_entities') recipe.entityCodes = [...op.entityCodes];
    if (op.operation === 'set_period') recipe.period = op.period;
    if (op.operation === 'add_panel') {
      demand(!keys.has(op.panelKey));
      recipe.panels.push(pair(PANEL_REGISTRY_V3.find(p => p.panelKey === op.panelKey)));
      keys.add(op.panelKey);
      if (op.panelKey === 'cohort_trajectory') recipe.viewPreferences.trajectoryMode = DEFAULT_TRAJECTORY_MODE;
    }
    if (op.operation === 'remove_panel') {
      demand(keys.has(op.panelKey)); recipe.panels = recipe.panels.filter(p => p.panelKey !== op.panelKey);
      keys.delete(op.panelKey);
    }
    if (op.operation === 'set_display_mode') {
      demand(recipe.panels.some(p => p.panelKey === op.panelKey)); recipe.viewPreferences.trajectoryMode = op.displayMode;
    }
    if (op.operation === 'reorder_panels') {
      demand(op.order.length === recipe.panels.length && op.order.every(key => recipe.panels.some(p => p.panelKey === key)));
      recipe.panels = op.order.map(key => recipe.panels.find(p => p.panelKey === key));
    }
  }
  const result = canonicalizeHistoricalDashboardRecipeV3(recipe);
  return { recipe: result, patch, diff: historicalDashboardRecipeDiff(base, result),
    calculationPlan: historicalDashboardCalculationPlan(base, result) };
}
export function historicalDashboardRecipeDiff(beforeRecipe, afterRecipe) {
  const before = beforeRecipe === null ? null : effectiveHistoricalDashboardRecipeAny(beforeRecipe);
  const after = effectiveHistoricalDashboardRecipeAny(afterRecipe);
  const beforeKeys = before?.panels.map(p => p.panelKey) ?? [], afterKeys = after.panels.map(p => p.panelKey);
  const change = (a, b) => same(a, b) ? null : { before: a, after: b };
  return {
    schemaVersion: 'historical-dashboard-revision-diff/v1',
    entityCodes: change(before?.entityCodes ?? null, after.entityCodes), period: change(before?.period ?? null, after.period),
    addedPanels: PANEL_KEYS_V3.filter(k => afterKeys.includes(k) && !beforeKeys.includes(k)),
    removedPanels: PANEL_KEYS_V3.filter(k => beforeKeys.includes(k) && !afterKeys.includes(k)),
    panelOrder: change(before ? beforeKeys : null, afterKeys),
    trajectoryMode: change(before?.viewPreferences.trajectoryMode ?? null, after.viewPreferences.trajectoryMode),
  };
}
export function historicalDashboardCalculationPlan(beforeRecipe, afterRecipe) {
  const before = beforeRecipe === null ? null : effectiveHistoricalDashboardRecipeAny(beforeRecipe);
  const after = effectiveHistoricalDashboardRecipeAny(afterRecipe);
  const a = before?.panels.map(p => p.panelKey) ?? [], b = after.panels.map(p => p.panelKey);
  const scopeChanged = !before || !same(before.entityCodes, after.entityCodes) || before.period !== after.period;
  return { scopeChanged, reuseCandidateKeys: b.filter(k => !scopeChanged && a.includes(k)),
    recomputeKeys: b.filter(k => scopeChanged || !a.includes(k)), removedKeys: a.filter(k => !b.includes(k)) };
}
export function historicalDashboardComparisonBasis(recipe) {
  const normalized = recipe?.schemaVersion === HISTORICAL_DASHBOARD_RECIPE_V3_SCHEMA
    ? canonicalizeHistoricalDashboardRecipeV3(recipe)
    : canonicalizeHistoricalDashboardRecipeV2(recipe);
  return ['full_history', 'since_entry'].includes(normalized.period) ? 'entry' : 'period_start';
}
export function historicalDashboardRecipeV2Sha256(recipe) {
  return sha256Hex(stableJson(canonicalizeHistoricalDashboardRecipeV2(recipe)));
}
export function historicalDashboardRecipeV3Sha256(recipe) {
  return sha256Hex(stableJson(canonicalizeHistoricalDashboardRecipeV3(recipe)));
}
export function historicalDashboardRecipeSha256(recipe) {
  if (recipe?.schemaVersion === HISTORICAL_DASHBOARD_RECIPE_V3_SCHEMA) return historicalDashboardRecipeV3Sha256(recipe);
  return historicalDashboardRecipeV2Sha256(canonicalizeHistoricalDashboardRecipeV2(
    recipe?.schemaVersion === HISTORICAL_DASHBOARD_RECIPE_V2_SCHEMA ? recipe : effectiveHistoricalDashboardRecipe(recipe),
  ));
}
export function historicalDashboardRecipesEffectivelyMatch(a, b) {
  try { return same(effectiveHistoricalDashboardRecipeAny(a), effectiveHistoricalDashboardRecipeAny(b)); } catch { return false; }
}
